import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import NoDataAvailable from './NoDataAvailable';
import { Col, Row } from 'react-bootstrap';
import { createQueryString, formatDateRange } from '../../../../../lib/utils';
import { fetchNumberOfIterationsPerTestCycle } from '../../../../../modules/dashboard';
import ChartTitle from '../../../../elements/ChartTitle';

const GRAPH_TITLE = 'Test Cases Executed In Each Iteration By Test Cycle';

const CustomTooltipCycle = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const dataObject = payload[0].payload;
    const cycleFullName = dataObject['Cycle Full Name'] || label || 'N/A';
    
    const displayedCycleName = cycleFullName.length > 50
      ? cycleFullName.substring(0, 50) + '...'
      : cycleFullName;
    return (
      <div className="custom-tooltip bg-white p-2 border border-gray-300 shadow-md text-sm rounded-lg">
        <p className="font-bold text-gray-800">{`${displayedCycleName}`}</p>
        <p className="text-gray-700">{`${payload[0].name}: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

const NumberOfIterationInTestCycle = ({ projectId, dateFilters }) => {
  const [selectedCycle, setSelectedCycle] = useState('');

  const queryString = createQueryString(dateFilters);

  const {
    data: testCycleData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['iterationsPerTestCycle', projectId, queryString],
    queryFn: () => fetchNumberOfIterationsPerTestCycle(projectId, queryString),
    select: (res) => res?.data || [],
    enabled: !!projectId,
  });

  useEffect(() => {
    if (testCycleData.length > 0 && !selectedCycle) {
      setSelectedCycle(testCycleData[0].id);
    }
  }, [testCycleData, selectedCycle]);

  const transformedData = useCallback(() => {
    if (!selectedCycle) return [];

    const cycle = testCycleData.find((c) => c.id === selectedCycle);
    if (!cycle?.iteration_details) return [];

    const statusColors = {};
    const iterationMap = {};

    cycle.iteration_details.forEach((iteration) => {
      const name = iteration.name.trim().replace(/\s+/g, ' ');
      if (!iterationMap[name]) {
        iterationMap[name] = {
          'Iteration Name': name,
          'Test Cases': 0,
        };
      }

      iteration.executions?.forEach((exec) => {
        const status = exec.status;
        const color = exec.status_color;
        iterationMap[name][status] = (iterationMap[name][status] || 0) + 1;
        iterationMap[name]['Test Cases'] += 1;
        statusColors[status] = color;
      });
    });

    const result = Object.values(iterationMap);
    result.statusColors = statusColors;
    return result.sort((a, b) => {
      const getNum = (s) => parseInt(s.match(/\d+/)?.[0] || '0', 10);
      return getNum(a['Iteration Name']) - getNum(b['Iteration Name']);
    });
  }, [testCycleData, selectedCycle]);

  const totalIterationsData = useMemo(() => {
    return testCycleData?.map((cycle) => ({
      'Test Cycle': cycle.key, 
      'Cycle Full Name': `${cycle.key}: ${cycle.name}`, 
      'Total Iterations': cycle.iteration_details?.length || 0,
    }));
  }, [testCycleData]);

  const handleCycleChange = (e) => setSelectedCycle(e.target.value);

  const finalData = transformedData();
  const statusColors = finalData.statusColors || {};
  const totalIterations = totalIterationsData;

  return (
    <Row>
      <Col md="12">
        <div className="chart-div">
          <ChartTitle title="Iterations By Test Cycle" dateFilters={dateFilters} />
          {isLoading || isError || totalIterations?.length === 0 ? (
            <NoDataAvailable />
          ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  barSize={25}
                  data={totalIterations}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="Test Cycle"
                    label={{
                      value: 'Test Cycles',
                      position: 'insideBottom',
                      offset: -10,
                    }}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip content={<CustomTooltipCycle />} />
                  <Legend wrapperStyle={{ padding: 10 }} />
                  <Bar dataKey="Total Iterations" fill='#7d3c98' />
                </BarChart>
              </ResponsiveContainer>
          )}
        </div>
      </Col>
      <Col md="12">
        <div className="chart-div">
          {isLoading || isError || finalData?.length === 0 ? (
            <NoDataAvailable body={GRAPH_TITLE} />
          ) : (
            <>
              <h5 className="text-dark text-wrap">
                Test Cases Executed In Each Iteration
              </h5>
              <select
                  onChange={handleCycleChange}
                  value={selectedCycle}
                  style={{
                    height: '2em',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    padding: '0 px',
                    width: '600px',
                    fontSize: '14px',
                  }}>
                  <option value="" disabled>
                    Select Test Cycle
                  </option>
                  {testCycleData?.map((cycle) => (
                    <option key={cycle.id} value={cycle.id}>
                      {cycle.key}-{cycle.name}
                    </option>
                  ))}
                </select>
              <p
                className="text-muted mb-3 mt-2"
                style={{ fontSize: '12px' }}>
                {formatDateRange(dateFilters)}
              </p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  barSize={25}
                  data={finalData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="Iteration Name"
                    label={{
                      value: 'Iterations',
                      position: 'insideBottom',
                      offset: -10,
                    }}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ padding: 10 }} />
                  <Bar dataKey="Test Cases" fill="#707b7c" name="Test Cases" />
                  {Object.keys(statusColors).map((status) => (
                    <Bar
                      key={status}
                      dataKey={status}
                      stackId="a"
                      name={status}
                      fill={statusColors[status] || '#8884d8'}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default NumberOfIterationInTestCycle;